import { useCallback, useEffect, useRef } from 'react'
import {
  brakingAngle,
  landingTrajectory,
  launchAngle,
  SPIN_RAMP_MS,
} from './rouletteGeometry'

type Flight = {
  elapsed: number
  previousFrame?: number
  settledAt?: number
  startAngle: number
  count: number
  target?: number
  brake?: { start: number; angle: number; end: number; duration: number }
  resolve?: (completed: boolean) => void
}

export function useRouletteMotion(onTick: () => void) {
  const rotorRef = useRef<SVGGElement>(null)
  const pointerRef = useRef<SVGGElement>(null)
  const angleRef = useRef(0)
  const frameRef = useRef(0)
  const flightRef = useRef<Flight | null>(null)
  const tickRef = useRef(onTick)
  useEffect(() => {
    tickRef.current = onTick
  }, [onTick])

  const cancel = useCallback(() => {
    cancelAnimationFrame(frameRef.current)
    flightRef.current?.resolve?.(false)
    flightRef.current = null
    pointerRef.current?.setAttribute('transform', 'rotate(0 250 24)')
  }, [])
  useEffect(() => cancel, [cancel])
  useEffect(() => {
    const resetClock = () => {
      if (flightRef.current) flightRef.current.previousFrame = undefined
    }
    document.addEventListener('visibilitychange', resetClock)
    return () => document.removeEventListener('visibilitychange', resetClock)
  }, [])

  const start = useCallback(
    (count: number) => {
      cancel()
      const flight: Flight = {
        count,
        elapsed: 0,
        startAngle: angleRef.current,
      }
      flightRef.current = flight
      let lastTick = -Infinity
      let pointerKick = 0
      const draw = (time: number) => {
        if (flightRef.current !== flight) return
        // The profile keeps the wheel mounted inside an inactive, hidden panel.
        // Only visible frames count, including the final pause before the result.
        const rotor = rotorRef.current
        if (document.hidden || !rotor || rotor.closest('[hidden]')) {
          flight.previousFrame = undefined
          frameRef.current = requestAnimationFrame(draw)
          return
        }
        const delta =
          flight.previousFrame === undefined
            ? 0
            : Math.min(50, Math.max(0, time - flight.previousFrame))
        flight.previousFrame = time
        flight.elapsed += delta
        const elapsed = flight.elapsed
        // Keep the final position visible across paint opportunities before revealing.
        if (flight.settledAt !== undefined) {
          if (elapsed - flight.settledAt >= 160) {
            flightRef.current = null
            flight.resolve?.(true)
          } else frameRef.current = requestAnimationFrame(draw)
          return
        }
        // This spin communicates the draw; reduced motion only removes decoration.
        let angle = flight.startAngle + launchAngle(elapsed)
        if (flight.target !== undefined && !flight.brake && elapsed >= SPIN_RAMP_MS) {
          const trajectory = landingTrajectory(angle, flight.target, count)
          flight.brake = {
            start: elapsed,
            angle,
            ...trajectory,
          }
        }
        let complete = false
        if (flight.brake) {
          const progress = (elapsed - flight.brake.start) / flight.brake.duration
          complete = progress >= 1
          angle = brakingAngle(flight.brake.angle, flight.brake.end, progress)
        }
        const sectorAngle = 360 / count
        if (
          Math.floor(angle / sectorAngle) !==
            Math.floor(angleRef.current / sectorAngle) &&
          elapsed - lastTick > 45
        ) {
          lastTick = elapsed
          pointerKick = elapsed
          tickRef.current()
        }
        angleRef.current = angle
        rotor.setAttribute('transform', `rotate(${angle % 360} 250 250)`)
        const kick = !pointerKick ? 0 : Math.max(0, 1 - (elapsed - pointerKick) / 120)
        pointerRef.current?.setAttribute(
          'transform',
          `rotate(${-12 * kick * kick} 250 24)`
        )
        if (complete) {
          angleRef.current = angle % 360
          pointerRef.current?.setAttribute('transform', 'rotate(0 250 24)')
          flight.settledAt = elapsed
        }
        frameRef.current = requestAnimationFrame(draw)
      }
      frameRef.current = requestAnimationFrame(draw)
    },
    [cancel]
  )

  const land = useCallback(
    (position: number) =>
      new Promise<boolean>((resolve) => {
        const flight = flightRef.current
        if (!flight) {
          resolve(false)
          return
        }
        flight.target = Number.isFinite(position)
          ? Math.max(1, Math.min(flight.count, Math.trunc(position)))
          : 1
        flight.resolve = resolve
      }),
    []
  )

  return { rotorRef, pointerRef, start, land, cancel }
}
